/**
 * CommandPalette — macOS Spotlight / Linear ⌘K launcher.
 *
 * Global hotkey:  Cmd/Ctrl+K (open), Esc (close), ↑/↓ (navigate), Enter (pick).
 * Sources (all in parallel, best-effort, gracefully skipped on error):
 *   • Static pages (dashboard / study / leaderboard / admin-students / …)
 *   • Live students (admin only) — GET /api/admin/students
 *   • Live batches (admin only)  — GET /api/admin/batches
 *
 * Designed to live at the very top of React's portal tree so its z-index is
 * never clipped by the dock, nav, or corner cluster.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import {
  Search, LayoutDashboard, Users, GraduationCap, FileText, BookOpen,
  Trophy, MessageSquare, Settings as SettingsIcon, Bookmark, Zap,
  UserCheck, Bell, ShieldCheck, CornerDownLeft, Mail,
} from "lucide-react";
import { API, useAuth } from "../contexts/AuthContext";

// Global pub/sub so any button anywhere can open the palette without prop-drilling
const OPEN_EVT = "lw:open-command-palette";
export function openCommandPalette() {
  window.dispatchEvent(new CustomEvent(OPEN_EVT));
}

const STUDENT_PAGES = [
  { label: "Dashboard",         hint: "Home",                to: "/student",                     icon: LayoutDashboard },
  { label: "Study",             hint: "Subjects & lectures", to: "/student/study",               icon: BookOpen },
  { label: "Revision",          hint: "Saved MCQs",          to: "/student/revision",            icon: Bookmark },
  { label: "Flashcards",        hint: "Spaced repetition",   to: "/student/flashcards",          icon: Zap },
  { label: "Streak",            hint: "Daily accountability", to: "/student/streak",             icon: Bell },
  { label: "Streak leaderboard", hint: "Consistency ranks",  to: "/student/streak-leaderboard",  icon: Trophy },
  { label: "Leaderboard",       hint: "Performance ranks",   to: "/student/leaderboard",         icon: Trophy },
  { label: "Mentor",            hint: "Your MBBS guide",     to: "/student/mentor",              icon: MessageSquare },
  { label: "Community",         hint: "Peer discussion",     to: "/student/community",           icon: Users },
  { label: "Settings",          hint: "Account & password",  to: "/student/settings",            icon: SettingsIcon },
];
const ADMIN_PAGES = [
  { label: "Command deck",  hint: "Overview",          to: "/admin",                icon: LayoutDashboard },
  { label: "Registrations", hint: "Approve / reject",  to: "/admin/registrations",  icon: UserCheck },
  { label: "Students",      hint: "All enrolled",      to: "/admin/students",       icon: Users },
  { label: "Batches",       hint: "Cohorts",           to: "/admin/batches",        icon: GraduationCap },
  { label: "Syllabus",      hint: "Content tree",      to: "/admin/content",        icon: BookOpen },
  { label: "MCQs",          hint: "Question bank",     to: "/admin/mcqs",           icon: FileText },
  { label: "Banners",       hint: "Hero carousel",     to: "/admin/banners",        icon: FileText },
  { label: "Mentors",       hint: "Assign & review",   to: "/admin/mentors",        icon: MessageSquare },
  { label: "Notifications", hint: "Broadcast",         to: "/admin/notifications",  icon: Bell },
  { label: "Bulk Email",    hint: "Send branded campaigns", to: "/admin/bulk-email", icon: Mail },
  { label: "Leaderboards",  hint: "Top performers",    to: "/admin/leaderboards",   icon: Trophy },
  { label: "Admin team",    hint: "Sub-admins, RBAC",  to: "/admin/team",           icon: ShieldCheck },
  { label: "Settings",      hint: "Your profile",      to: "/admin/settings",       icon: SettingsIcon },
];

export default function CommandPalette() {
  const { user } = useAuth();
  const role = user?.role;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // ⌘K / Ctrl+K global hotkey + external open event
  useEffect(() => {
    const h = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape") setOpen(false);
    };
    const openHandler = () => setOpen(true);
    window.addEventListener("keydown", h);
    window.addEventListener(OPEN_EVT, openHandler);
    return () => {
      window.removeEventListener("keydown", h);
      window.removeEventListener(OPEN_EVT, openHandler);
    };
  }, []);

  // Lazy-load admin data once when first opened
  useEffect(() => {
    if (!open || role !== "admin" || students.length || batches.length) return;
    axios.get(`${API}/admin/students?limit=200`).then(r => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setStudents(arr);
    }).catch(() => {});
    axios.get(`${API}/admin/batches`).then(r => {
      setBatches(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
  }, [open, role, students.length, batches.length]);

  // Focus input on open + reset state
  useEffect(() => {
    if (open) {
      setQ(""); setIdx(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  const pages = role === "admin" ? ADMIN_PAGES : STUDENT_PAGES;

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const score = (hay) => {
      const h = String(hay || "").toLowerCase();
      if (!needle) return 1;
      if (h.startsWith(needle)) return 3;
      if (h.includes(needle)) return 2;
      return 0;
    };
    const out = [];
    // Pages
    for (const p of pages) {
      const s = Math.max(score(p.label), score(p.hint));
      if (s > 0) out.push({ kind: "page", key: `p-${p.to}`, label: p.label, hint: p.hint, icon: p.icon, to: p.to, s });
    }
    // Students (admin)
    if (role === "admin" && needle) {
      for (const u of students) {
        const s = Math.max(score(u.name), score(u.email));
        if (s > 0) out.push({
          kind: "student", key: `s-${u.id}`, label: u.name || u.email,
          hint: u.email || (u.phone ? u.phone : "student"),
          icon: Users, to: `/admin/students?focus=${u.id}`, s,
        });
      }
    }
    // Batches (admin)
    if (role === "admin" && needle) {
      for (const b of batches) {
        const s = Math.max(score(b.name), score(b.slug));
        if (s > 0) out.push({
          kind: "batch", key: `b-${b.id}`, label: b.name,
          hint: `${b.slug || "batch"} · ${b.student_count ?? 0} students`,
          icon: GraduationCap, to: `/admin/batches?focus=${b.id}`, s,
        });
      }
    }
    out.sort((a, b) => b.s - a.s);
    return out.slice(0, 40);
  }, [q, pages, role, students, batches]);

  useEffect(() => { setIdx(0); }, [q]);

  const pick = (i) => {
    const it = results[i];
    if (!it) return;
    setOpen(false);
    navigate(it.to);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, Math.max(0, results.length - 1))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(idx); }
  };

  if (!role) return null;

  const palette = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          className="fixed inset-0 flex items-start justify-center pt-[12vh] px-4"
          style={{
            background: "rgba(15, 23, 42, 0.4)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 9999,
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="cmd-palette-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            className="w-full max-w-[620px] rounded-[18px] overflow-hidden"
            style={{
              background: "rgba(255, 255, 255, 0.94)",
              backdropFilter: "blur(24px) saturate(180%)",
              WebkitBackdropFilter: "blur(24px) saturate(180%)",
              border: "1px solid rgba(255, 255, 255, 0.9)",
              boxShadow: "0 30px 80px -20px rgba(15, 23, 42, 0.40), 0 10px 30px -10px rgba(124, 58, 237, 0.25)",
            }}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid="cmd-palette"
          >
            <div className="flex items-center gap-3 px-4 h-14 border-b" style={{ borderColor: "var(--px-line)" }}>
              <Search size={16} style={{ color: "var(--px-ink-mute)" }} />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKey}
                placeholder={role === "admin" ? "Jump to page, student, or batch…" : "Jump to any page…"}
                className="flex-1 bg-transparent outline-none text-[15px] font-medium"
                style={{ color: "var(--px-ink)" }}
                data-testid="cmd-palette-input"
              />
              <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                   style={{ background: "var(--px-paper-soft)", color: "var(--px-ink-mute)", border: "1px solid var(--px-line)" }}>
                ESC
              </kbd>
            </div>

            <div className="max-h-[52vh] overflow-y-auto" data-testid="cmd-palette-list">
              {results.length === 0 ? (
                <div className="p-10 text-center text-sm" style={{ color: "var(--px-ink-mute)" }}>
                  No matches for <strong>"{q}"</strong>.
                </div>
              ) : (
                results.map((r, i) => {
                  const Icon = r.icon || Search;
                  const active = i === idx;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onMouseEnter={() => setIdx(i)}
                      onClick={() => pick(i)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition"
                      style={{
                        background: active ? "var(--px-rose-soft)" : "transparent",
                        color: "var(--px-ink)",
                      }}
                      data-testid={`cmd-palette-item-${r.key}`}
                    >
                      <div
                        className="w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0"
                        style={{ background: active ? "rgba(124, 58, 237, 0.12)" : "var(--px-paper-soft)" }}
                      >
                        <Icon size={14} style={{ color: active ? "var(--px-rose)" : "var(--px-ink-mute)" }} strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13.5px] font-semibold truncate">{r.label}</div>
                        <div className="text-[11.5px] truncate" style={{ color: "var(--px-ink-mute)" }}>{r.hint}</div>
                      </div>
                      <span className="text-[9.5px] font-mono uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
                            style={{ background: r.kind === "page" ? "var(--px-info-soft)" : r.kind === "student" ? "var(--px-success-soft)" : "var(--px-warn-soft)",
                                     color: r.kind === "page" ? "var(--px-info)" : r.kind === "student" ? "var(--px-success)" : "var(--px-warn)" }}>
                        {r.kind}
                      </span>
                      {active && <CornerDownLeft size={12} style={{ color: "var(--px-ink-mute)" }} />}
                    </button>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-between px-4 h-10 text-[10.5px] font-mono uppercase tracking-[0.14em]"
                 style={{ background: "var(--px-paper-soft)", color: "var(--px-ink-mute)", borderTop: "1px solid var(--px-line)" }}>
              <span>↑↓ navigate · ↵ select</span>
              <span>{results.length} result{results.length === 1 ? "" : "s"}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(palette, document.body);
}
